/*

Verto FreeSWITCH interface

Copyright (c) 2019 Roman Yerin <r.yerin@640kb.co.uk>

All rights reserved.

Redistribution and use in source and binary forms, with or without
modification, are permitted provided that the following conditions are met:
    * Redistributions of source code must retain the above copyright
      notice, this list of conditions and the following disclaimer.
    * Redistributions in binary form must reproduce the above copyright
      notice, this list of conditions and the following disclaimer in the
      documentation and/or other materials provided with the distribution.
    * Neither the name of the <organization> nor the
      names of its contributors may be used to endorse or promote products
      derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL <COPYRIGHT HOLDER> BE LIABLE FOR ANY
DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
(INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
(INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.

*/

/*

Interface to JSON RPC Client constructor

*/

export interface JsonRpcClientParams {
  socketUrl: string
  ajaxUrl?: string
  onmessage?: { (event: Object): void }
  login: string
  passwd: string
  sessid?: string
  loginParams?: Object
  userVariables?: Object
}

/*

Interface to JSON RPC requests

*/

interface JsonRpcRequest {
  id: number
  jsonrpc: string
  method: string
  params: Object
}

/*

Interface to JSON RPC replies

*/

interface JsonRpcReply {
  id: number
  jsonrpc: string
  method?: string
  params?: JsonRpcParams
  result?: any
  error?: {
    code: number
    message: string
    data?: {
      error: string
      errorCode: string
    }
  }
}

/*

Interface to JSON RPC calls

*/

interface JsonRpcCall {
  request: JsonRpcRequest
  success_cb?: { (data: Object): void }
  error_cb?: { (data: JsonRpcReply['error']): void }
}

interface JsonRpcParams {
  callID: string
  sdp?: string
  caller_id_number: string
  caller_id_name: string
  callee_id_number: string
  callee_id_name: string
}

/*

JSON RPC Client

*/
enum CloseEventCode {
  Normal = 1000,
}

class JsonRpcClient {
  options: JsonRpcClientParams

  private request_id: number = 1

  private socket_!: WebSocket

  private queue: Array<string> = []

  private callbacks: Array<JsonRpcCall> = []

  private eventHandler!: { (method: string, params: JsonRpcParams): void }

  private reconnectHandler!: { (): void }

  private debug: boolean | undefined = false

  private closed: boolean = false

  private initSocket_() {
    if (this.closed) return

    this.socket_ = new WebSocket(this.options.socketUrl)
    this.socket_.onmessage = this.onMessage.bind(this)
    // TODO: Implement auto reconnect attepts
    this.socket_.onclose = (ev: CloseEvent) => {
      if (ev.code === CloseEventCode.Normal) return // 1000 means that connection was closed normally
      setTimeout(() => {
        this.initSocket_()
      }, 1000)
    }
    this.socket_.onopen = () => {
      let req: string = ''
      if (this.reconnectHandler) this.reconnectHandler()

      while ((req = this.queue.pop() as any)) {
        this.socket.send(req)
      }
    }
  }

  private closeSocket_() {
    this.socket_.close(CloseEventCode.Normal)
    this.closed = true
  }

  private get socket(): WebSocket {
    if (this.socket_) {
      return this.socket_
    }
    this.initSocket_()
    return this.socket_
  }

  constructor(options: JsonRpcClientParams, debug?: boolean) {
    this.debug = debug
    this.options = { ...options }
  }

  private onMessage(msg: MessageEvent) {
    // Check if this is JSON RPC

    let response: JsonRpcReply = {
      id: 0,
      jsonrpc: '',
      params: {
        callID: '',
        caller_id_number: '',
        caller_id_name: '',
        callee_id_number: '',
        callee_id_name: '',
      },
      error: {
        code: 0,
        message: '',
      },
    }
    try {
      response = JSON.parse(msg.data)
    } catch {
      // Got something else, just ignore in case
    }

    if (typeof response === 'object' && 'jsonrpc' in response && response.jsonrpc === '2.0') {
      if ('method' in response) {
        this.eventHandler(response.method || '', response?.params as any)
      } else if ('result' in response && this.callbacks[response.id]) {
        // We've just got response
        const { success_cb = () => {} } = this.callbacks[response.id]
        delete this.callbacks[response.id]
        success_cb({ ...response.result })
      } else if (response.error && response.error.code !== 0 && this.callbacks[response.id]) {
        const { error_cb = () => {} } = this.callbacks[response.id]
        const failed_request = { ...this.callbacks[response.id] }

        delete this.callbacks[response.id]

        if (response?.error?.code === -32000 && this.options.login && this.options.passwd) {
          // Auth is needed
          this.call(
            'login',
            {
              login: this.options.login,
              passwd: this.options.passwd,
              loginParams: this.options.loginParams,
              userVariables: this.options.userVariables,
            },
            () => {
              // Re-send failed request
              this.socketCall_(failed_request)
            },
            () => {
              // Auth failed
              error_cb(response.error)
            },
          )
        } else {
          error_cb({ ...response.error })
        }
      }
    }
  }

  private socketCall_({ request, success_cb, error_cb }: JsonRpcCall) {
    request.id = this.request_id++

    const rawData = JSON.stringify(request)

    this.callbacks[request.id] = { request, success_cb, error_cb }

    if (this.socket.readyState < 1) {
      // Socket is not ready, queue message
      this.queue.push(rawData)
    } else {
      this.socket.send(rawData)
    }
  }

  public setEventHandler(handler: { (method: string, params: JsonRpcParams): void }) {
    this.eventHandler = handler
  }

  public setReconnectHandler(handler: { (): void }) {
    this.reconnectHandler = handler
  }

  public call(
    method: string,
    params?: Object,
    success_cb?: { (data: any): void },
    error_cb?: { (data: JsonRpcReply['error']): void },
  ) {
    // Construct the JSON-RPC 2.0 request.
    const call_params = { ...params }

    const request = {
      jsonrpc: '2.0',
      method,
      params: call_params,
      id: 0,
    }

    if (this.socket) {
      this.socketCall_({ request: request as any, success_cb, error_cb })
    }
  }

  public close() {
    this.queue = []
    this.callbacks = []
    this.eventHandler = () => {}
    this.reconnectHandler = () => {}
    this.closeSocket_()
  }
}

export { JsonRpcClient }
